"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Layers, Microscope, ShieldCheck, SlidersHorizontal as Sliders, Wrench, ArrowRight, Sparkles, Gauge } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
export default function Processes() {
  const prefersReducedMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("fabrication");
  const processSteps = [{
    id: "step-1",
    number: "01",
    title: "Seleção e preparação de ligas metálicas",
    description: "Aços inoxidáveis austeníticos, ligas de latão, titânio e materiais especiais inspecionados para máxima resistência à abrasão e corrosão química.",
    badge: "Matéria-prima"
  }, {
    id: "step-2",
    number: "02",
    title: "Estampagem e conformação de alta cadência",
    description: "Prensas de alta precisão com matrizes calibradas para geometrias complexas e acabamento sem rebarbas em orifícios micrométricos.",
    badge: "Formação Mecânica"
  }, {
    id: "step-3",
    number: "03",
    title: "Maquinação CNC e perfuração micrométrica",
    description: "Tolerâncias de precisão dimensional rigorosas para assegurar o perfil exato do cone de pulverização e caudal projetado.",
    badge: "Maquinação"
  }, {
    id: "step-4",
    number: "04",
    title: "Controlo dimensional e ensaios hidrodinâmicos",
    description: "Verificação ótica a laser e testes de padrão de pulverização sob condições reais de pressão e caudal antes do envio.",
    badge: "Garantia de Qualidade"
  }];
  const qualityStandards = [{
    title: "Tolerâncias micrométricas",
    detail: "Desvio máximo controlado abaixo de +/- 5 micrómetros nos núcleos e orifícios de pulverização.",
    icon: Gauge
  }, {
    title: "Inspeção ótica 3D a laser",
    detail: "Varredura completa de geometrias internas e externas contra ficheiros CAD originais.",
    icon: Microscope
  }, {
    title: "Ensaios de fluxo e distribuição",
    detail: "Medição do tamanho de gota (DSD) e uniformidade volumétrica em laboratório calibrado.",
    icon: Sliders
  }, {
    title: "Conformidade ISO e rastreabilidade",
    detail: "Certificação de lotes e rastreio de matéria-prima desde a fundição até à montagem final.",
    icon: ShieldCheck
  }];
  return <section id="manufacturingandqualityprocesses" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 md:mb-20">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-mono text-xs text-secondary border-secondary/30">
            Processos Industriais & Controlo
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight mb-5">
            Fabrico de precisão e garantia de qualidade rigorosa
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Conheça as etapas de conformação, estampagem metálica e os ensaios de tolerância aplicados a cada componente para garantir eficiência e vida útil prolongada.
          </p>
        </div>

        {/* Dynamic Tabs Section */}
        <Tabs defaultValue="fabrication" value={activeTab} onValueChange={setActiveTab} className="w-full mb-16">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 border-b border-border pb-4">
            <TabsList className="bg-muted p-1 rounded-lg">
              <TabsTrigger value="fabrication" className="px-4 py-2 text-sm font-medium">
                <Wrench className="w-4 h-4 mr-2" />
                Fabrico & Estampagem
              </TabsTrigger>
              <TabsTrigger value="quality" className="px-4 py-2 text-sm font-medium">
                <ShieldCheck className="w-4 h-4 mr-2" />
                Controlo de Qualidade
              </TabsTrigger>
              <TabsTrigger value="materials" className="px-4 py-2 text-sm font-medium">
                <Layers className="w-4 h-4 mr-2" />
                Ligas & Materiais
              </TabsTrigger>
            </TabsList>

            <span className="text-xs font-mono text-muted-foreground hidden sm:inline-block">
              Spraying Systems Co. Standard
            </span>
          </div>

          {/* Tab 1: Fabrication Steps */}
          <TabsContent value="fabrication" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps.map((step, i) => <div key={step.id} data-index={i}>
                  <Card className="h-full border border-border/80 bg-card hover:border-secondary/40 transition-[border-color,box-shadow] duration-200 shadow-sm flex flex-col justify-between">
                    <CardHeader className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-mono font-bold text-secondary">
                          {step.number}
                        </span>
                        <Badge variant="secondary" className="text-2xs font-normal">
                          {step.badge}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg font-default font-semibold text-foreground leading-snug">
                        {step.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                        {step.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </div>)}
            </div>
          </TabsContent>

          {/* Tab 2: Quality Assurance & Metrology */}
          <TabsContent value="quality" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {qualityStandards.map((item, i) => {
                const Icon = item.icon;
                return <div key={item.title} data-index={i}>
                      <Card className="h-full border border-border bg-card shadow-xs">
                        <CardHeader className="space-y-2 pb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center text-accent-foreground mb-1">
                            <Icon className="w-5 h-5" />
                          </div>
                          <CardTitle className="text-base font-semibold text-foreground">
                            {item.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {item.detail}
                          </p>
                        </CardContent>
                      </Card>
                    </div>;
              })}
              </div>

              <div className="lg:col-span-5 bg-primary text-primary-foreground rounded-xl p-8 flex flex-col justify-between shadow-md">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-secondary" />
                    <span className="text-xs uppercase tracking-wider font-mono text-primary-foreground/80">
                      Garantia de Repetibilidade
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold font-default mb-4 leading-tight">
                    Desempenho uniforme em cada pulverizador
                  </h3>
                  <p className="text-primary-foreground/90 text-sm leading-relaxed mb-6">
                    A precisão no corte e conformação assegura que cada bico atinge exatamente o ângulo de pulverização, a granulometria das gotas e a taxa de fluxo tabelada, eliminando desperdícios de fluidos na linha de produção.
                  </p>
                  <ul className="space-y-2.5 text-sm text-primary-foreground/90 mb-8">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-secondary shrink-0" />
                      <span>Certificados de calibração por lote</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-secondary shrink-0" />
                      <span>Redução comprovada de desgaste por erosão</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-secondary shrink-0" />
                      <span>Relatórios de ensaio dimensional disponíveis</span>
                    </li>
                  </ul>
                </div>

                <Link to="/TechnicalSolutionsPage">
                  <Button variant="secondary" className="w-full justify-between">
                    <span>Consultar soluções técnicas</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </TabsContent>

          {/* Tab 3: Materials & Alloys */}
          <TabsContent value="materials" className="mt-0">
            <div className="bg-card border border-border rounded-xl p-6 sm:p-8 shadow-xs">
              <div className="max-w-2xl mb-6">
                <h3 className="text-xl font-bold text-foreground mb-2">
                  Ligas metálicas e polímeros de engenharia
                </h3>
                <p className="text-sm text-muted-foreground">
                  Cada ambiente industrial exige uma resistência específica a químicos agressivos, temperaturas extremas ou desgaste mecânico.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div data-index={0} className="p-4 rounded-lg bg-muted/50 border border-border">
                  <span className="font-semibold text-foreground block mb-1">Aços Inoxidáveis 303, 316 e 316L</span>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Ideal para a indústria alimentar, farmacêutica e química geral com alta resistência à oxidação.
                  </p>
                </div>
                <div data-index={1} className="p-4 rounded-lg bg-muted/50 border border-border">
                  <span className="font-semibold text-foreground block mb-1">Hastelloy, Titânio e Ligas de Níquel</span>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Aplicações em ambientes de extrema corrosão ácida e altas temperaturas térmicas.
                  </p>
                </div>
                <div data-index={2} className="p-4 rounded-lg bg-muted/50 border border-border">
                  <span className="font-semibold text-foreground block mb-1">Latão, Carboneto de Tungsténio e PVDF</span>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Elevada resistência ao desgaste por partículas abrasivas em suspensão e fluidos cáusticos.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Bottom Technical Callout */}
        <div className="rounded-xl border border-border bg-card p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h4 className="text-lg font-semibold text-foreground">
              Precisa de peças por medida ou especificações técnicas detalhadas?
            </h4>
            <p className="text-sm text-muted-foreground">
              A nossa equipa técnica apoia na seleção do bico e processo de fabrico adequado para o seu sistema.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/CatalogsPage">
              <Button variant="outline">
                Ver catálogos
              </Button>
            </Link>
            <Link to="/ContactsPage">
              <Button variant="default">
                Falar com especialista
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}